// ============================================================================
// Vendor Dashboard total + filter toolbar. Presentational — all state lives in
// the page; this renders the controls and calls back (same split as
// `../logistics/board_controls`).
//
// Unlike the board's strip, the only chip here is "All · N" — the count of
// bookings in scope. The board's per-bucket attention chips (Failed / Needs
// booking / Needs confirm / Needs vehicle / Awaiting proof / In flight) and its
// SLA-breached chip are deliberately absent: those are the ops desk's own work
// queues, and this page is read as trips, not as a queue. Each row still carries
// its attention + SLA overlay in the Status column, so nothing is hidden — only
// the filtering-by-bucket affordance is gone.
//
// A leaner bar than the board's on purpose. What is here scopes WHICH TRIPS you
// are looking at (day, city, warehouse, vendor, express, search); what is not here
// is the ops desk's own machinery — Owner, "group by" (this page is always grouped
// by trip) and the trip-assignment / auto-assign buttons. Carrier and Show-cancelled
// are gone too: the transporter is named by VENDOR on this page, and a cancelled
// truck is not a trip anyone is running.
// ============================================================================
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, Select, Space, Switch, Tag, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { WarehouseOption } from "../logistics/logistics.api";
import { MILE_TITLE, MileSlice } from "./summary";

// The cities MD runs a desk in — a fixed list, not one derived from the rows on screen, so the
// choices are the same every day (including a day one city happens to be empty). The server
// resolves each to its delivery-pincode belt rather than matching the address's city text.
const CITY_OPTIONS = [
  { value: "Bangalore", label: "Bangalore" },
  { value: "Hyderabad", label: "Hyderabad" },
  // Not a city: customers no hub serves (off the hub pincode list, over 100 km from every hub) —
  // the same rows the "Outstation client" tag is on. The server resolves the value, not the label.
  { value: "outstation", label: "Outstation client" },
];

interface Props {
  summary?: { total: number }; // the scope's booking total — the 'All · N' chip
  attention: string;
  setAttention: (v: string) => void;
  dateRange: [Dayjs, Dayjs];
  setDateRange: (v: [Dayjs, Dayjs]) => void;
  city: string | undefined;
  setCity: (v: string | undefined) => void;
  warehouse: number | undefined;
  setWarehouse: (v: number | undefined) => void;
  warehouses: WarehouseOption[];
  expressOnly: boolean;
  setExpressOnly: (v: boolean) => void;
  vendor: string | undefined;
  setVendor: (v: string | undefined) => void;
  vendorOptions: string[]; // the vendors present in the loaded scope
  driver: string | undefined;
  setDriver: (v: string | undefined) => void;
  driverOptions: string[]; // the drivers present in the loaded scope (narrowed by vendor)
  mile: MileSlice | undefined;
  setMile: (v: MileSlice | undefined) => void;
  deliveryState: "delivered" | "not_delivered" | undefined;
  setDeliveryState: (v: "delivered" | "not_delivered" | undefined) => void;
  searchInput: string;
  setSearchInput: (v: string) => void;
  filtersApplied: boolean;
  // Downloads the CURRENT scope as a CSV — the same dates and filters the board is showing, not the
  // page of trips on screen. Owned by the page, because the scope object is.
  onExport: () => void;
  exporting: boolean;
  clearFilters: () => void;
  onRefresh: () => void;
  loading: boolean;
}

const VendorControls: React.FC<Props> = (p) => (
  <>
    {p.summary && (
      <Space wrap size={8} style={{ marginBottom: 12 }}>
        <Tag.CheckableTag
          checked={!p.attention}
          onChange={() => p.setAttention("")}
          style={{ border: "1px solid #eee", padding: "2px 10px" }}
        >
          All · {p.summary.total}
        </Tag.CheckableTag>
      </Space>
    )}

    {/* Two rows, in the order the desk works: WHO and WHAT first (vendor, city, delivery status,
        warehouse, order type, express), then WHEN and the narrowing tools (date, driver, search).
        Actions stay pinned right, on the second row. */}
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <div>
        {/* The transporter running the trip, read off `trip_vendor_name` — the vendor Loginext
            stamps on the trip payload (see `vendorOf`). Options are whatever is on the board in this
            scope, so a new vendor needs no code change. Narrows in the browser — every row is
            already loaded. */}
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Vendor</div>
        <Select
          allowClear
          showSearch
          placeholder="All vendors"
          style={{ width: 160 }}
          value={p.vendor}
          onChange={(v) => p.setVendor(v ?? undefined)}
          onClear={() => p.setVendor(undefined)}
          options={p.vendorOptions.map((v) => ({ value: v, label: v }))}
          filterOption={(input, opt) =>
            String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Delivery city</div>
        <Select
          allowClear
          placeholder="All cities"
          value={p.city}
          style={{ width: 150 }}
          options={CITY_OPTIONS}
          onChange={(v) => p.setCity(v ?? undefined)}
        />
      </div>
      <div>
        {/* Delivered vs still out, on the SAME test the Summary tiles use. A trip keeps its heading
            while its stops are filtered, so "not delivered" reads as what each vehicle still owes. */}
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Delivery status</div>
        <Select
          allowClear
          placeholder="All"
          style={{ width: 150 }}
          value={p.deliveryState}
          onChange={(v) => p.setDeliveryState(v ?? undefined)}
          onClear={() => p.setDeliveryState(undefined)}
          options={[
            { value: "not_delivered", label: "Not delivered" },
            { value: "delivered", label: "Delivered" },
          ]}
        />
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Warehouse</div>
        <Select
          allowClear
          placeholder="All warehouses"
          value={p.warehouse}
          style={{ width: 190 }}
          onChange={(v) => p.setWarehouse(v ?? undefined)}
          options={p.warehouses.map((w) => ({
            value: w.id,
            label: w.city ? `${w.label} · ${w.city}` : w.label,
          }))}
        />
      </div>
      <div>
        {/* Which half of the operation. Trips are often MIXED — an inbound pickup and a customer
            drop on one truck — so this narrows the shipments inside a trip rather than dropping
            whole trips: a mixed trip keeps its heading and shows only the half you asked for, and a
            trip that is entirely the other half falls out because none of its rows survive. Labels
            come from the Summary's own vocabulary, so the filter and the split bar read alike. */}
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Order type</div>
        <Select
          allowClear
          placeholder="Pick + deliver"
          style={{ width: 160 }}
          value={p.mile}
          onChange={(v) => p.setMile(v ?? undefined)}
          onClear={() => p.setMile(undefined)}
          options={[
            { value: "first_mile", label: MILE_TITLE.first_mile },
            { value: "last_mile", label: MILE_TITLE.last_mile },
          ]}
        />
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Express only</div>
        <div style={{ height: 32, display: "flex", alignItems: "center" }}>
          <Switch checked={p.expressOnly} onChange={p.setExpressOnly} />
        </div>
      </div>
    </div>

    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <div>
        {/* A WINDOW, not the board's "on or before": this page keeps delivered trips, and an
            unbounded lower end would reach back through every delivery ever completed. */}
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Dispatch between</div>
        <DatePicker.RangePicker
          allowClear={false}
          format="DD MMM"
          value={p.dateRange}
          onChange={(v) => v && v[0] && v[1] && p.setDateRange([v[0], v[1]])}
          presets={[
            { label: "Today", value: [dayjs(), dayjs()] as [Dayjs, Dayjs] },
            { label: "Yesterday", value: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")] as [Dayjs, Dayjs] },
            { label: "Last 7 days", value: [dayjs().subtract(6, "day"), dayjs()] as [Dayjs, Dayjs] },
            { label: "Tomorrow", value: [dayjs().add(1, "day"), dayjs().add(1, "day")] as [Dayjs, Dayjs] },
          ]}
        />
      </div>
      <div>
        {/* The trip's stamped driver, full name. Options narrow to the picked vendor's drivers, and
            like Vendor this filters in the browser over rows already loaded. */}
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Driver</div>
        <Select
          allowClear
          showSearch
          placeholder="All drivers"
          style={{ width: 200 }}
          value={p.driver}
          onChange={(v) => p.setDriver(v ?? undefined)}
          onClear={() => p.setDriver(undefined)}
          options={p.driverOptions.map((d) => ({ value: d, label: d }))}
          filterOption={(input, opt) =>
            String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Search</div>
        <Input.Search
          placeholder="Carrier ref, ENQ or PO number…"
          allowClear
          style={{ width: 260 }}
          value={p.searchInput}
          onChange={(e) => p.setSearchInput(e.target.value)}
        />
      </div>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "flex-end", gap: 8 }}>
        {p.filtersApplied && <Button onClick={p.clearFilters}>Clear</Button>}
        {/* Says "these filters", not "this page": the file covers the whole filtered range, which is
            the one thing about an export people assume wrongly. */}
        <Tooltip title="Download every shipment in the current filters as a CSV — not just this page">
          <Button icon={<DownloadOutlined />} onClick={p.onExport} loading={p.exporting}>
            CSV
          </Button>
        </Tooltip>
        <Tooltip title="Refresh now">
          <Button icon={<ReloadOutlined />} onClick={p.onRefresh} loading={p.loading} />
        </Tooltip>
      </div>
    </div>
  </>
);

export default VendorControls;
